import Cookies from "js-cookie";
import { useCallback, useEffect, useMemo, useState } from "react";

import { API_GET_TIME_ALLOCATED_OVERVIEW } from "../../../config/Endpoints.js";
import type { PeriodRange } from "../../components/features/time-allocated-overview/types.ts";
import { periodRangeToApiDates } from "../../utils/period-range.ts";
import type { TimesheetStatsResponse } from "../../components/features/timesheet/types.ts";
import { getApi } from "../../utils/api.ts";

function unwrapStatsPayload(raw: unknown): TimesheetStatsResponse {
  if (
    raw &&
    typeof raw === "object" &&
    "data" in raw &&
    (raw as { data: unknown }).data !== undefined &&
    (raw as { data: unknown }).data !== null
  ) {
    return (raw as { data: TimesheetStatsResponse }).data;
  }
  return raw as TimesheetStatsResponse;
}

export function useTimeAllocatedStats(
  employeeId: string,
  range: PeriodRange,
) {
  const [data, setData] = useState<TimesheetStatsResponse | null>(null);
  /** True while the first/ongoing request runs (no-params load when no employee is selected). */
  const [loading, setLoading] = useState(() =>
    Boolean(periodRangeToApiDates(range)),
  );
  const [error, setError] = useState<string | null>(null);

  const rangeKey = useMemo(() => JSON.stringify(range), [range]);
  const accessToken = Cookies.get("accessToken");

  const fetchStats = useCallback(
    async (options?: { signal?: AbortSignal }) => {
      const signal = options?.signal;
      const r = JSON.parse(rangeKey) as PeriodRange;
      const dates = periodRangeToApiDates(r);

      if (!dates) {
        setData(null);
        setLoading(false);
        setError(null);
        return;
      }

      const trimmedId = employeeId?.trim() ?? "";

      if (!trimmedId) {
        setData(null);
        setLoading(false);
        setError(null);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const qs = new URLSearchParams({
          employeeId: trimmedId,
          startDate: dates.from,
          endDate: dates.to,
        }).toString();
        const url = `${API_GET_TIME_ALLOCATED_OVERVIEW}?${qs}`;
        const { data: raw } = await getApi<unknown>(
          url,
          accessToken,
          "json",
          signal,
        );
        if (signal?.aborted) return;
        setData(unwrapStatsPayload(raw));
      } catch (e) {
        if (e instanceof Error && e.name === "AbortError") return;
        if (signal?.aborted) return;
        setError(e instanceof Error ? e.message : "Failed to load overview");
        setData(null);
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [employeeId, rangeKey, accessToken],
  );

  useEffect(() => {
    const controller = new AbortController();
    void fetchStats({ signal: controller.signal });
    return () => controller.abort();
  }, [fetchStats]);

  return { data, loading, error, refetch: () => void fetchStats() };
}
